import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Target, Eye, Handshake, CheckCircle2 } from "lucide-react";
import { company, aboutImage, stats } from "../mock/mock";

const values = [
  { icon: Target, title: "Our Mission", text: "To make the best possible parts for your product \u2014 balancing price, quality and delivery speed with full control of our in-house facility." },
  { icon: Eye, title: "Our Vision", text: "To be the manufacturing partner our customers trust to take complete ownership of a project, from concept to doorstep." },
  { icon: Handshake, title: "Our Promise", text: "Unwavering focus on quality at every step, with transparent communication and on-time delivery, every time." },
];

const About = () => {
  return (
    <div className="bg-white">
      {/* Page header */}
      <section className="bg-[#141414] py-16">
        <div className="max-w-[1140px] mx-auto px-5">
          <p className="text-[#D80000] font-heading font-semibold tracking-widest text-sm">ABOUT US</p>
          <h1 className="font-heading text-4xl md:text-5xl font-bold text-white mt-3">Everything Made In-House.</h1>
          <p className="text-white/60 mt-4 max-w-2xl">Your Global Partner in Manufacturing.</p>
        </div>
      </section>

      {/* Intro */}
      <section className="py-16 md:py-20">
        <div className="max-w-[1140px] mx-auto px-5 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <h2 className="font-heading text-3xl md:text-[36px] font-semibold text-[#1a1a1a] leading-tight">
              Why Choose {company.name}?
            </h2>
            <p className="text-[#1a1a1a]/80 leading-8 mt-5">
              {company.name} is an India-based engineering and manufacturing
              company delivering reliable, precision-engineered components to
              OEMs, engineering companies, equipment manufacturers, industrial
              buyers, contractors and distributors. Our core capabilities span
              CNC &amp; VMC machining, aluminium die casting, fiber laser
              cutting, sheet metal fabrication and precision components.
            </p>
            <p className="text-[#1a1a1a]/80 leading-8 mt-4">
              Operating from a 10,000+ sq. ft. facility in Vasai East with a
              100+ strong team of engineers, technicians and production
              personnel, we take complete ownership of your project &mdash; from
              individual components and prototypes to regular production &mdash;
              so your parts arrive on time and virtually flawless.
            </p>
            <ul className="mt-6 space-y-3">
              {["Fully equipped in-house facility", "Full project ownership", "Rigorous quality control", "Fastest possible turnaround"].map((item) => (
                <li key={item} className="flex items-center gap-3 text-[#1a1a1a]">
                  <CheckCircle2 className="w-5 h-5 text-[#D80000] shrink-0" /> {item}
                </li>
              ))}
            </ul>
          </div>
          <img src={aboutImage} alt="In-house manufacturing facility" className="rounded-lg shadow-xl w-full h-[420px] object-cover" />
        </div>
      </section>

      {/* Values */}
      <section className="bg-[#f7f7f8] py-16 md:py-20">
        <div className="max-w-[1140px] mx-auto px-5 grid grid-cols-1 md:grid-cols-3 gap-8">
          {values.map((v) => (
            <div key={v.title} className="bg-white rounded-lg p-8 shadow-sm hover:shadow-lg transition-shadow">
              <span className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-[#D80000]/10">
                <v.icon className="w-7 h-7 text-[#D80000]" strokeWidth={1.8} />
              </span>
              <h3 className="font-heading text-xl font-semibold text-[#1a1a1a] mt-5">{v.title}</h3>
              <p className="text-[#1a1a1a]/75 leading-relaxed mt-3">{v.text}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Stats */}
      <section className="py-14">
        <div className="max-w-[1100px] mx-auto px-5 grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
          {stats.map((st) => (
            <div key={st.label}>
              <p className="font-heading text-4xl md:text-5xl font-bold text-[#D80000]">{st.value}</p>
              <p className="text-sm text-[#1a1a1a]/70 mt-2">{st.label}</p>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[#D80000] py-16">
        <div className="max-w-[820px] mx-auto px-5 text-center">
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-white">Ready to build something great?</h2>
          <Link to="/contact" className="inline-flex items-center gap-2 mt-7 bg-white text-[#D80000] font-semibold px-8 py-3.5 rounded-md hover:bg-[#1a1a1a] hover:text-white">
            Get in Touch <ArrowRight className="w-5 h-5" />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default About;
